import { Button, Icon, makeStyles, Theme, ButtonGroup, ButtonProps } from '@material-ui/core';
import { useScreenSizeCheck } from '@moderepo/mode-app-components';
import clsx from 'clsx';
import React, { CSSProperties, useCallback, useState } from 'react';
import { EntitiesListingViewType, EntitiesFiltersConfigPanelProps, EntitiesFiltersConfigPanel } from '../../components';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectProject } from '../../utils/dataStateHooks';


const useStyle = makeStyles((theme: Theme) => {

    return {
        root: {
            width        : '100%',
            display      : 'flex',
            flexDirection: 'column',
            alignItems   : 'center',
        },

        topRow: {
            flex          : 1,
            width         : '100%',
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            flexWrap      : 'wrap',
            padding       : theme.spacing(1),
        },

        bottomRow: {
            flex      : 1,
            width     : '100%',
            background: theme.palette.background.paper,
        },

        rightActionsBar: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'flex-end',
            flex          : 1,
        },

        rightAction: {
            marginLeft: theme.spacing(1),
            whiteSpace: 'nowrap',
        },

        toggleButton: {
            color        : theme.palette.text.secondary,
            '&[disabled]': {
                color: theme.palette.text.primary,
            },
        },

        searchPanel: {
            width     : '100%',
            padding   : theme.spacing(1),
            '&.hidden': {
                display: 'none',
            },
        },

        [theme.breakpoints.up('md')]: {
            topRow: {
                padding: theme.spacing(2),
            },
            rightAction: {
                marginLeft: theme.spacing(2),
            },
        },

        [theme.breakpoints.up('lg')]: {
            topRow: {
                padding: theme.spacing(2, 3, 2, 3),
            },
            bottomRow: {
                '& [class*="searchPanel"]': {
                    padding: theme.spacing(2, 3, 2, 3),
                },
            },
            rightAction: {
                marginLeft: theme.spacing(3),
            },
        },
    };
}, { name: 'EntitiesListingControlBar', index: 1 });



export interface CustomAction {
    readonly label: string;
    readonly icon?: string | undefined;
    readonly disabled?: boolean | undefined;
    readonly color?: ButtonProps['color'] | undefined;
    readonly onClick: (event: React.MouseEvent)=> void;
}



export interface EntitiesListingControlBarProps {
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly projectId: number;

    readonly showViewTypeOptions?: boolean | undefined;
    readonly selectedViewType: EntitiesListingViewType;
    readonly onViewTypeSelected: (viewType: EntitiesListingViewType)=> void;

    readonly createEntityButton?: CustomAction | undefined;
    readonly finderButton?: CustomAction | undefined;
    readonly infoButton?: CustomAction | undefined;

    // Whether to show search and filters options on init
    readonly expandSearchAndFilters?: boolean | undefined;

    // OPTIONAL search and filter settings
    readonly searchAndFilterSettings?: {

        // To show homeId filter option. This filter is only needed if homeId is not preselected.
        readonly homeIdFilterSettings?: EntitiesFiltersConfigPanelProps['homeIdFilterSettings'];

        // To show entityClass filter option.
        readonly entityClassesFilterSettings?: EntitiesFiltersConfigPanelProps['entityClassesFilterSettings'];

        // To show ancestor filter option. This filter is only needed if parent is not preselected.
        readonly parentFilterSettings?: EntitiesFiltersConfigPanelProps['parentFilterSettings'];

        // To show entity attribute filter option.
        readonly attributesFilterSettings?: EntitiesFiltersConfigPanelProps['attributesFilterSettings']

        readonly texts: EntitiesFiltersConfigPanelProps['texts'],

        // Whether or not to automatically close the search/filter panel right away when the user click Apply
        readonly closeOnApply?: boolean | undefined;

        readonly onFilterChanged: EntitiesFiltersConfigPanelProps['onFilterChanged'];

        // Callback handler to handle the search/filter when the user click on the "Apply" button. The handler will be provided with the
        // filter values the user selected, homeId, or entityClass, or both if both options are enabled.
        readonly onApplySearchAndFilters: EntitiesFiltersConfigPanelProps['onApplySearchAndFilters'];
    }
}


export const EntitiesListingControlBar: React.FC<EntitiesListingControlBarProps> = (props: EntitiesListingControlBarProps) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const isTabletOrLarger = useScreenSizeCheck('md');
    const [showFiltersPanel, setShowFiltersPanel] = useState<boolean>(Boolean(props.expandSearchAndFilters));


    /**
     * To show/hide filters panel. If 'show' is specifically set to true/false then we will set the 'showFiltersPanel' to that value.
     * Else, we will toggle the 'showFiltersPanel'.
     */
    const onShowFiltersPanel = useCallback((show?: boolean) => {
        setShowFiltersPanel((currentValue) => {
            if (show !== undefined) {
                return show;
            }
            return !currentValue;
        });
    }, []);


    return (
        <div className={clsx(props.className, classes.root)}>
            <div className={classes.topRow}>

                <div className={classes.rightActionsBar}>
                    {/*
                        By default, we will always show the view type option unless showViewTypeOptions is specifically set to false
                    */}
                    {props.showViewTypeOptions !== false && (
                        <ButtonGroup
                            className={clsx(classes.rightAction)}
                            variant="contained"
                            color="default"
                        >
                            <Button
                                className={classes.toggleButton}
                                disabled={props.selectedViewType === EntitiesListingViewType.TREE}
                                onClick={() => {
                                    props.onViewTypeSelected(EntitiesListingViewType.TREE);
                                }}
                            >
                                <Icon>account_tree</Icon>
                            </Button>
                            <Button
                                className={classes.toggleButton}
                                disabled={props.selectedViewType === EntitiesListingViewType.TABLE}
                                onClick={() => {
                                    props.onViewTypeSelected(EntitiesListingViewType.TABLE);
                                }}
                            >
                                <Icon>table_chart</Icon>
                            </Button>
                            <Button
                                className={classes.toggleButton}
                                disabled={props.selectedViewType === EntitiesListingViewType.LIST}
                                onClick={() => {
                                    props.onViewTypeSelected(EntitiesListingViewType.LIST);
                                }}
                            >
                                <Icon>format_list_bulleted</Icon>
                            </Button>
                            <Button
                                className={classes.toggleButton}
                                disabled={props.selectedViewType === EntitiesListingViewType.GRID}
                                onClick={() => {
                                    props.onViewTypeSelected(EntitiesListingViewType.GRID);
                                }}
                            >
                                <Icon>apps</Icon>
                            </Button>
                        </ButtonGroup>
                    )}

                    {/*
                        If there is searchAndFilter options, show the button to toggle the search bar
                    */}
                    {props.searchAndFilterSettings && (
                        <Button
                            className={clsx(classes.rightAction)}
                            variant="contained"
                            color="default"
                            startIcon={isTabletOrLarger ? <Icon>{showFiltersPanel ? 'search_off' : 'search'}</Icon> : undefined}
                            onClick={() => {
                                onShowFiltersPanel();
                            }}
                        >
                            {isTabletOrLarger ? (
                                trans('label.search')
                            ) : (
                                <Icon>{showFiltersPanel ? 'search_off' : 'search'}</Icon>
                            )}
                        </Button>
                    )}


                    {/*
                        If finderButton is provided, show the button
                    */}
                    {props.finderButton && (
                        <Button
                            className={clsx(classes.rightAction)}
                            variant="contained"
                            color={props.finderButton.color ?? 'default'}
                            disabled={props.finderButton.disabled}
                            startIcon={isTabletOrLarger && props.finderButton.icon ? <Icon>{props.finderButton.icon}</Icon> : undefined}
                            onClick={(event) => {
                                if (props.finderButton) {
                                    props.finderButton.onClick(event);
                                }
                            }}
                        >
                            {isTabletOrLarger || !props.finderButton.icon ? (
                                // Show button label for medium screen or larger or if there is no icon
                                trans(props.finderButton.label)
                            ) : (
                                <Icon>{props.finderButton.icon}</Icon>
                            )}
                        </Button>
                    )}

                    {/*
                        If infoButton is provided, show the button
                    */}
                    {props.infoButton && (
                        <Button
                            className={clsx(classes.rightAction)}
                            variant="contained"
                            color={props.infoButton.color ?? 'default'}
                            disabled={props.infoButton.disabled}
                            startIcon={isTabletOrLarger && props.infoButton.icon ? <Icon>{props.infoButton.icon}</Icon> : undefined}
                            onClick={(event) => {
                                if (props.infoButton) {
                                    props.infoButton.onClick(event);
                                }
                            }}
                        >
                            {isTabletOrLarger || !props.infoButton.icon ? (
                                // Show button label for medium screen or larger or if there is no icon
                                trans(props.infoButton.label)
                            ) : (
                                <Icon>{props.infoButton.icon}</Icon>
                            )}
                        </Button>
                    )}

                    {/*
                        If createEntityButton is provided, show the button
                    */}
                    {props.createEntityButton && (
                        <Button
                            className={clsx(classes.rightAction)}
                            variant="contained"
                            color={props.createEntityButton.color ?? 'default'}
                            disabled={props.createEntityButton.disabled}
                            startIcon={isTabletOrLarger && props.createEntityButton.icon ? <Icon>{props.createEntityButton.icon}</Icon> : undefined}
                            onClick={(event) => {
                                if (props.createEntityButton) {
                                    props.createEntityButton.onClick(event);
                                }
                            }}
                        >
                            {isTabletOrLarger || !props.createEntityButton.icon ? (
                                // Show button label for medium screen or larger or if there is no icon
                                trans(props.createEntityButton.label)
                            ) : (
                                <Icon>{props.createEntityButton.icon}</Icon>
                            )}
                        </Button>
                    )}

                </div>
            </div>

            {/*
                Add this filters toolbar if at least 1 of the filter options is enabled
            */}
            {props.searchAndFilterSettings && (
                props.searchAndFilterSettings.homeIdFilterSettings
                || props.searchAndFilterSettings.entityClassesFilterSettings
                || props.searchAndFilterSettings.attributesFilterSettings
            ) && (
                <div className={classes.bottomRow}>
                    <EntitiesFiltersConfigPanel
                        className={clsx(classes.searchPanel, !showFiltersPanel && 'hidden')}
                        onFilterChanged={props.searchAndFilterSettings.onFilterChanged}
                        homeIdFilterSettings={props.searchAndFilterSettings.homeIdFilterSettings}
                        entityClassesFilterSettings={props.searchAndFilterSettings.entityClassesFilterSettings}
                        parentFilterSettings={props.searchAndFilterSettings.parentFilterSettings}
                        attributesFilterSettings={props.searchAndFilterSettings.attributesFilterSettings}
                        texts={props.searchAndFilterSettings.texts}
                        onApplySearchAndFilters={(filters) => {
                            if (props.searchAndFilterSettings) {
                                props.searchAndFilterSettings.onApplySearchAndFilters(filters);
                                if (props.searchAndFilterSettings.closeOnApply) {
                                    onShowFiltersPanel(false);
                                }
                            }
                        }}
                        onCloseSearchAndFilters={() => {
                            onShowFiltersPanel(false);
                        }}
                    />
                </div>
            )}
        </div>
    );
};
